import { ClipboardIcon, EnvelopeIcon, EyeIcon, ShareIcon } from '@heroicons/react/24/outline'
import { Fragment, useMemo, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { Tooltip } from 'react-tooltip'
import { ScheduleEmailStatusEnum, SectionRole, SectionSlot, SectionSlotLead, User } from '../../../../gql/graphql'
import { APP_ADMIN, APP_EDIT_USER_AVAILABILITIES, APP_SCHEDULE_SEASON_NOTICE } from '../../../_constants/permissions'
import { Dialog } from '../../../_ui/Dialog'
import { EditAvailabilities } from '../../EditAvailabilities'
import { EditUserSeasonNotice } from '../../EditUserSeasonNotice'
import { useSchedule } from '../../ScheduleContext'
import { useMyself } from '../../UserContext'

type PropTypes = {
  user?: User
  loading?: boolean
  onSendScheduleEmail?: () => void
  onSendScheduleChangeEmail?: () => void
  sendingScheduleEmail?: boolean
  sendingScheduleChangeEmail?: boolean
}

type SourceType = SectionSlot & SectionSlotLead

export const Schedule = ({ user, loading, onSendScheduleEmail, onSendScheduleChangeEmail, sendingScheduleEmail, sendingScheduleChangeEmail }: PropTypes) => {
  const { season } = useSchedule() || {}
  const { canAccess } = useMyself() || {}
  const [ editSeasonNoticeOpen, setEditSeasonNoticeOpen ] = useState(false)
  const [ availabilityDialogOpen, setAvailabilityDialogOpen ] = useState(false)
  const [ sendScheduleEmailOpen, setSendScheduleEmailOpen ] = useState(false)
  const [ sendScheduleChangeEmailOpen, setSendScheduleChangeEmailOpen ] = useState(false)
  const scheduleEmailStatus = user?.currentSeasonCheckin?.scheduleEmailStatus
  const isScheduleEmailVisible = scheduleEmailStatus === ScheduleEmailStatusEnum.Changed || scheduleEmailStatus === ScheduleEmailStatusEnum.Checkin
  const isScheduleEmailChanged = scheduleEmailStatus === ScheduleEmailStatusEnum.Changed
  const isSendingScheduleEmail = isScheduleEmailChanged ? sendingScheduleChangeEmail : sendingScheduleEmail

  const scheduleItems = useMemo(() => {
    return ([
      ...(user?.acceptedSlots || []).map(item => ({...item, typename: 'SectionSlot'})),
      ...(user?.acceptedSlotLeads || []).map(item => ({...item, typename: 'SectionSlotLead'})),
    ] as SourceType[]).reduce((acc: any, item: SourceType) => {
        return {
          ...acc,
          [(item.begin || '').slice(0, 10)]: [
            ...(acc[(item.begin || '').slice(0, 10)] || []),
            item
          ].sort((a, b) => (a?.begin || '').localeCompare((b?.begin || '')))
        }
      }, {})
  }, [user?.acceptedSlotLeads, user?.acceptedSlots])

  const collapsedAcceptedSlots = useMemo(() => {
    return Object.keys(scheduleItems).reduce((acc: any, date) => {
      return {
        ...acc,
        // @ts-ignore
        [date]: (scheduleItems[date] || []).reduce((acc2: any, slot: (SectionSlot & {typename: string}) | (SectionSlotLead & {typename: string})) => {
          if (slot.typename === acc2[0]?.typename && slot.begin === acc2[0]?.end && slot.sectionRole?.id === acc2[0]?.sectionRole?.id) {
            return [
              {
                ...acc2[0],
                id: acc2[0].id + slot.id,
                end: slot.end
              },
              ...(acc2.slice(1) || [])
            ]
          } else {
            return [
              slot,
              ...acc2
            ]
          }
        }, []).reverse()
      }
    }, {})
  }, [scheduleItems])

  const rolesWithShiftExemption = useMemo(() => {
    return [
      ...(user?.sectionRoles || []).filter(item => item?.season === season?.id && item?.shiftExemption),
      ...((season?.sections || [])
          .filter(seasonSection => {
            return (user?.sections || []).findIndex(s => s?.id === seasonSection?.id) >= 0
              && seasonSection?.season === season?.id
          })
          .reduce((acc: SectionRole[], item) => {
            return [
              ...acc,
              ...(item?.sectionRoles || []).filter(role => role.shiftExemption)
            ]
          }, [])
      )
    ].sort((a, b) => (a?.title || '').localeCompare(b?.title || ''))
  }, [season?.id, season?.sections, user?.sectionRoles, user?.sections])

  // @ts-ignore
  // @ts-ignore
  return (
    <div className={'sm:grid sm:grid-cols-3 sm:gap-2'}>
      <div className={'text-[14px]'}>
        <div className={'flex items-center gap-2 flex-wrap'}>
          Schichten
          {canAccess && canAccess(APP_SCHEDULE_SEASON_NOTICE) && (
            <div className={'cursor-pointer'} onClick={() => setAvailabilityDialogOpen(true)}>
              <EyeIcon className={'w-4 h-4'}/>
            </div>
          )}
          {canAccess && canAccess(APP_SCHEDULE_SEASON_NOTICE) && (
            <div className={'cursor-pointer'} onClick={() => setEditSeasonNoticeOpen(true)}>
              <ClipboardIcon className={'w-4 h-4'}/>
            </div>
          )}
          </div>
          {canAccess && canAccess(APP_ADMIN) && user?.currentSeasonCheckin && isScheduleEmailVisible && (
            <div className={'mt-1'}>
              <button
                type="button"
                onClick={isScheduleEmailChanged ? () => setSendScheduleChangeEmailOpen(true) : () => setSendScheduleEmailOpen(true)}
                disabled={isSendingScheduleEmail}
                className={`block inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                  isScheduleEmailChanged
                    ? 'bg-amber-100 text-amber-900 ring-amber-300 hover:bg-amber-200 cursor-pointer disabled:cursor-not-allowed disabled:hover:bg-amber-100'
                    : 'bg-emerald-100 text-emerald-900 ring-emerald-300 hover:bg-emerald-200 cursor-pointer disabled:cursor-not-allowed disabled:hover:bg-emerald-100'
                }`}
                title={isScheduleEmailChanged ? 'Schichtplan geändert - Änderungs-E-Mail senden' : 'Schichtplan freigegeben - E-Mail senden'}
              >
                <EnvelopeIcon className={'w-3 h-3 mr-1'} />
                {isSendingScheduleEmail ? 'Sende...' : isScheduleEmailChanged ? 'Geändert' : 'Schichtmail senden'}
              </button>
            </div>
          )}

        {user?.currentSeasonCheckin && !isScheduleEmailChanged && (
          <Dialog
            title={'Schichtplan-E-Mail senden?'}
            open={sendScheduleEmailOpen}
            onClose={() => setSendScheduleEmailOpen(false)}
          >
            <div className={'space-y-4'}>
              <p className={'text-sm text-gray-700'}>
                Diese Aktion sendet die normale Schichtplan-E-Mail an den Nutzer.
              </p>
              <div className={'flex justify-end gap-3'}>
                <button
                  type="button"
                  className={'rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50'}
                  onClick={() => setSendScheduleEmailOpen(false)}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className={'rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:bg-emerald-300'}
                  disabled={sendingScheduleEmail}
                  onClick={() => {
                    onSendScheduleEmail?.()
                    setSendScheduleEmailOpen(false)
                  }}
                >
                  {sendingScheduleEmail ? 'Sende...' : 'Senden'}
                </button>
              </div>
            </div>
          </Dialog>
        )}

        <Dialog
          separator
          title={'Verfügbarkeiten'}
          open={availabilityDialogOpen}
          onClose={() => setAvailabilityDialogOpen(false)}
        >
          <EditAvailabilities userID={user?.id} readonly={!canAccess || !canAccess(APP_EDIT_USER_AVAILABILITIES)} />
        </Dialog>

        {canAccess && canAccess(APP_SCHEDULE_SEASON_NOTICE) && editSeasonNoticeOpen && user && (
          <Dialog title={'Schichtplan Notiz bearbeiten'} open onClose={() => setEditSeasonNoticeOpen(false)}>
            <EditUserSeasonNotice user={user} />
          </Dialog>
        )}

        {user?.currentSeasonCheckin && isScheduleEmailChanged && (
          <Dialog
            title={'Schichtplan-Änderungs-E-Mail senden?'}
            open={sendScheduleChangeEmailOpen}
            onClose={() => setSendScheduleChangeEmailOpen(false)}
          >
            <div className={'space-y-4'}>
              <p className={'text-sm text-gray-700'}>
                Diese Aktion sendet die Schichtplan-Änderungs-E-Mail an den Nutzer.
              </p>
              <div className={'flex justify-end gap-3'}>
                <button
                  type="button"
                  className={'rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50'}
                  onClick={() => setSendScheduleChangeEmailOpen(false)}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className={'rounded-md bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-500 disabled:cursor-not-allowed disabled:bg-amber-300'}
                  disabled={sendingScheduleChangeEmail}
                  onClick={() => {
                    onSendScheduleChangeEmail?.()
                    setSendScheduleChangeEmailOpen(false)
                  }}
                >
                  {sendingScheduleChangeEmail ? 'Sende...' : 'Senden'}
                </button>
              </div>
            </div>
          </Dialog>
        )}
      </div>
      <div className={'col-span-2'}>
        <div className={'grid gap-2 text-sm'}>
          {loading && (
            <Skeleton width={150} />
          )}
          {!loading && user?.seasonNotice && (
            <div className={'bg-yellow-200 border border-yellow-300/40 rounded-lg p-2 -mx-2 text-gray-900'}>
              {user.seasonNotice}
            </div>
          )}
          {!loading && Object.keys(collapsedAcceptedSlots).length === 0 && (
            <div>
              Noch keine Schichten zugeordnet
            </div>
          )}
          {Object.keys(collapsedAcceptedSlots).sort().map(date => (
            <div key={date}>
              <h6 className={'font-bold mb-1'}>
                {new Date(date).toLocaleDateString('de-DE', {weekday: "long", day: "2-digit", month: "2-digit"})}
              </h6>
              {collapsedAcceptedSlots[date].map((item: SourceType) => (
                <div
                  key={item.id}
                  className={'grid grid-cols-[105px_1fr] gap-8'}
                  data-tooltip-id={item.id}
                  data-tooltip-content={item.__typename === 'SectionSlotLead' ? 'Schichtleitung' : ''}
                >
                  <div className={'flex gap-1 items-center'}>
                    {item?.isFlexSlot && (
                      <ShareIcon className={'w-3 h-3'} />
                    )}
                    <span>
                      {new Date((item.begin || '')?.replace(/-/g, '/')).toLocaleTimeString('de-DE', {hour: '2-digit', minute: '2-digit'})}
                    </span>
                    <span>-</span>
                    <span>
                      {new Date((item.end || '')?.replace(/-/g, '/')).toLocaleTimeString('de-DE', {hour: '2-digit', minute: '2-digit'})}
                    </span>
                  </div>
                  <div>
                    {item.sectionRole?.title || ''}
                    {item.__typename === 'SectionSlotLead' && (
                      <Fragment >
                        &nbsp;
                        <strong>*</strong>
                        <Tooltip id={item.id} />
                      </Fragment>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}

          {rolesWithShiftExemption.length > 0 && (
            <div>
              <h6 className={'font-bold mb-1'}>
                Rollen ohne Schichten
              </h6>
              {rolesWithShiftExemption.map(role => (
                <div className={'grid grid-cols-[105px_1fr] gap-8'}>
                  {role?.title}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
